// The main board's Group by (None / Project / Person) and its folded headings. What the board shows
// is in the URL, so a link reproduces it (`?group=project&fold=180,none`), and it is remembered per
// person and per board on this device. Pure, apart from the localStorage helpers (each wrapped: a private window,
// or a browser that blocks site data, may refuse them).

import { assigneeNames, projectCode, type BoardItem } from "@boem/core/shared";
import { NOBODY } from "./components/AssigneeFilter.tsx";
import { readOrgSetting, writeOrgSetting } from "./org.ts";

export type GroupBy = "none" | "project" | "person";
export type Grouped = Exclude<GroupBy, "none">;
export const GROUP_BY: GroupBy[] = ["none", "project", "person"];
export const GROUP_LABEL: Record<GroupBy, string> = { none: "None", project: "Project", person: "Person" };

/** The fold key of the "No project" group (a project's is its # number; a person's their name, NOBODY for nobody). */
export const NO_PROJECT = "none";

/** How the main board is grouped, and which headings are folded under each grouping. */
export interface BoardLook {
  group: GroupBy;
  folds: Record<Grouped, string[]>;
}

export const LOOK_DEFAULT: BoardLook = { group: "none", folds: { project: [], person: [] } };

const isGroup = (v: unknown): v is GroupBy => typeof v === "string" && (GROUP_BY as string[]).includes(v);
/** fold keys from anything: strings, no commas (the URL's separator), at most 100 */
const foldList = (v: unknown): string[] =>
  Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === "string" && x.length > 0 && x.length <= 120 && !x.includes(",")))].slice(0, 100) : [];

// -- Groups ---------------------------------------------------------------------------------

/** One project's small chip: "03 Delivery app" (the number, then the title). */
export const projectChipLabel = (p: Pick<BoardItem, "num" | "title" | "projectNo">) => `${p.projectNo != null ? projectCode(p.projectNo) : `#${p.num}`} ${p.title}`;

/** A project's steady colour: one of the five tone sets the avatars use (toneOf, app.css), by its project
 *  number, so neighbouring projects (01, 02, 03 ...) never share one until the sixth. */
export const projectTone = (p: Pick<BoardItem, "num" | "projectNo">) => Math.abs((p.projectNo ?? p.num) - 1) % 5;

/** Which groups an item falls in: its project; by person, each of the people it's with (OEM-285), the lead first. */
export function groupKeys(it: Pick<BoardItem, "project" | "assignee" | "assignees">, by: Grouped): string[] {
  if (by === "project") return [it.project ? String(it.project.num) : NO_PROJECT];
  const names = assigneeNames(it);
  return names.length ? names : [NOBODY];
}

/** The first group an item falls in (by person: its lead's). */
export const groupKey = (it: Pick<BoardItem, "project" | "assignee" | "assignees">, by: Grouped): string => groupKeys(it, by)[0]!;

export interface ItemGroup {
  key: string;
  by: Grouped;
  /** "03 Delivery app", "No project", "Rohit", "Unassigned" */
  label: string;
  /** by project: the project (its progress, ⧉ and ↗); null for "No project" */
  project: Pick<BoardItem, "num" | "title" | "projectNo" | "children" | "ownBoard"> | null;
  /** by person: their name; null for "Unassigned" */
  person: string | null;
  /** in the order they came (sorted already) */
  items: BoardItem[];
  /** how many of them are open */
  open: number;
  /** by person: how many of them are with someone else too (they show under each person, OEM-285) */
  shared?: number;
}

/**
 * The items under headings, keeping their order inside each (sort first, then group). By project:
 * the projects by their number, "No project" last. By person: most open items first, then by name,
 * "Unassigned" last. `projectOf` gives the project's own item (for its progress) when the board has it.
 */
export function groupItems(items: BoardItem[], by: Grouped, projectOf: (num: number) => BoardItem | undefined = () => undefined): ItemGroup[] {
  const m = new Map<string, ItemGroup>();
  for (const it of items) {
    const keys = groupKeys(it, by);
    for (const key of keys) {
      let g = m.get(key);
      if (!g) {
        const ref = by === "project" && it.project ? (projectOf(it.project.num) ?? { ...it.project, children: { total: 0, done: 0 } }) : null;
        g = {
          key,
          by,
          label: by === "project" ? (ref ? projectChipLabel(ref) : "No project") : key === NOBODY ? "Unassigned" : key,
          project: ref,
          person: by === "person" && key !== NOBODY ? key : null,
          items: [],
          open: 0,
          shared: 0,
        };
        m.set(key, g);
      }
      g.items.push(it);
      if (it.status === "open") g.open++;
      if (keys.length > 1) g.shared = (g.shared ?? 0) + 1;
    }
  }
  const groups = [...m.values()];
  const last = by === "project" ? NO_PROJECT : NOBODY;
  return groups.sort((a, b) => {
    if (a.key === last || b.key === last) return a.key === last ? 1 : -1;
    if (by === "project") return (a.project?.projectNo ?? Infinity) - (b.project?.projectNo ?? Infinity) || (a.project?.num ?? 0) - (b.project?.num ?? 0);
    return b.open - a.open || a.label.localeCompare(b.label);
  });
}

// -- The URL ---------------------------------------------------------------------------------

/** Our parameters; anything else in the URL (panels, What's new, ...) is left as it is. */
const KEYS = ["group", "fold"];

/** What a URL says about the grouping (null: nothing, so the person's remembered look applies). */
export function readLook(search: string): { group: GroupBy; fold: string[] } | null {
  const q = new URLSearchParams(search);
  const group = q.get("group");
  if (!isGroup(group)) return null;
  return { group, fold: group === "none" ? [] : foldList((q.get("fold") ?? "").split(",")) };
}

/** The query string with the grouping in it (only what differs from the default), or (null) taken out; "" or "?...". */
export function writeLook(search: string, look: BoardLook | null): string {
  const q = new URLSearchParams();
  for (const [k, v] of new URLSearchParams(search)) if (!KEYS.includes(k)) q.append(k, v);
  if (look && look.group !== "none") {
    q.set("group", look.group);
    const fold = look.folds[look.group];
    if (fold.length) q.set("fold", fold.join(","));
  }
  const s = q.toString().replace(/%3A/gi, ":").replace(/%2C/gi, ",");
  return s ? `?${s}` : "";
}

/** The look to open with: the URL's grouping and folds when it names one, else the remembered look. */
export function startLook(search: string, saved: BoardLook): BoardLook {
  const url = readLook(search);
  if (!url) return saved;
  return { group: url.group, folds: url.group === "none" ? saved.folds : { ...saved.folds, [url.group]: url.fold } };
}

// -- Remembered per person, per board ----------------------------------------------------------

const lookKey = (viewerId: string) => `buyoem.board.${viewerId}`;

/**
 * The look this person left this board's main view in on this device (the default when there's none,
 * or no storage). Per board (`buyoem.board.<person>.<board>`, as org.ts keeps every setting), so a
 * personal board and OEMUAP each keep their own grouping; OEMUAP takes over the key from before boards.
 */
export function savedLook(viewerId: string | null | undefined, org?: string): BoardLook {
  if (!viewerId) return LOOK_DEFAULT;
  try {
    const raw = JSON.parse(readOrgSetting(lookKey(viewerId), org) ?? "null") as { group?: unknown; folds?: { project?: unknown; person?: unknown } } | null;
    if (!raw || typeof raw !== "object") return LOOK_DEFAULT;
    return { group: isGroup(raw.group) ? raw.group : "none", folds: { project: foldList(raw.folds?.project), person: foldList(raw.folds?.person) } };
  } catch {
    return LOOK_DEFAULT;
  }
}

export function saveLook(viewerId: string | null | undefined, look: BoardLook, org?: string) {
  if (!viewerId) return;
  writeOrgSetting(lookKey(viewerId), JSON.stringify(look), org);
}

/** The look with one heading folded or unfolded (under the current grouping). */
export function toggleFold(look: BoardLook, key: string, folded?: boolean): BoardLook {
  if (look.group === "none") return look;
  const cur = look.folds[look.group];
  const on = folded ?? !cur.includes(key);
  const next = on ? (cur.includes(key) ? cur : [...cur, key]) : cur.filter((k) => k !== key);
  return next === cur ? look : { ...look, folds: { ...look.folds, [look.group]: next } };
}
